import {customElement, property} from "lit/decorators.js";
import {elementFrom} from "../../../common/utils/html";
import {UnLitElement} from "../../../common/elements";
import {TriggerService} from "../../../common/trigger/triggerService";
import {resolve} from "../../../container";
import {DialogActivationFactory} from "./dialogActivation";
import {LocalStorage} from "../../../common/clientStorage";
import {type DialogOpener, DialogOpenerFactory} from "./dialogOpener";

interface ClientStorageStatus {
    enabled?: boolean;
}

@customElement("eop-trigger-dialog")
export class EopTriggerDialog extends UnLitElement {

    @property({attribute: "data-trigger"})
    private trigger: string;
    @property({attribute: "active-from-breakpoint", type: String})
    public activeFromBreakpoint: string = "xs";
    @property({attribute: "client-storage-key", type: String})
    public clientStorageKey: string;

    private dialogOpener: DialogOpener;

    public constructor(
        private triggerService: TriggerService = resolve(TriggerService),
        private dialogActivationFactory: DialogActivationFactory = resolve(DialogActivationFactory),
        private localStorage: LocalStorage = resolve(LocalStorage),
        private dialogOpenerFactory: DialogOpenerFactory = resolve(DialogOpenerFactory)
    ) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();

        const dialogTemplate = this.resolveTemplateElement("content");

        if (!dialogTemplate) {
            return;
        }

        const dialogHtml = dialogTemplate.innerHTML;
        const dialogContent = elementFrom<HTMLElement>(dialogHtml);
        this.dialogOpener = this.dialogOpenerFactory.create(dialogContent);

        if (this.trigger) {
            this.bootStrap(dialogContent);
        }
    }

    public openDialog(e: Event): void {
        this.dialogOpener.openDialog(e);
    }

    private bootStrap(dialogContent: HTMLElement): void {
        const activation = this.dialogActivationFactory.create(this, dialogContent, this.activeFromBreakpoint);
        const openBootStrappedDialog = (): void => {
            if (!this.isEnabledAccordingToStorage()) {
                return;
            }
            this.dialogOpener.openDialog(new Event("bootstrapdialog"));
            this.disablePersistently();
        };
        this.triggerService.forEvent(this.trigger)
            .createTrigger(this, () => activation.ifActive(openBootStrappedDialog));
    }

    private isEnabledAccordingToStorage(): boolean {
        if (!this.clientStorageKey) {
            return true;
        }
        const storageContent = this.localStorage.fetch<ClientStorageStatus>(this.clientStorageKey);
        return storageContent?.enabled ?? true;
    }

    protected disablePersistently(): void {
        if (!this.clientStorageKey) {
            return;
        }
        const storageData: ClientStorageStatus = {enabled: false};
        this.localStorage.save(this.clientStorageKey, storageData);
    }
}